/**
 * Data Layer — Shared Helpers
 *
 * Internal utilities used by all domain modules.
 * NOT exported to consumers — only used within src/data/domains/*.
 */

import { safeStorageWrite } from '../utils/storageQuota';
import { STORAGE_KEY } from '../config/constants';

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * When API_BASE is set (non-empty string), the dataLayer switches to backend mode.
 * When empty/undefined, it uses localStorage.
 */
export const API_BASE = ''; // e.g. 'https://api.meesterdjay.nl/v1'

export const USE_BACKEND = Boolean(API_BASE);
export { STORAGE_KEY };

// ─── Internal Helpers ─────────────────────────────────────────────────────────

/** Generic localStorage read with JSON parse */
export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/** Generic localStorage write with JSON stringify */
export function writeJson(key: string, value: unknown): boolean {
  return safeStorageWrite(key, JSON.stringify(value));
}

/** Generic localStorage remove */
export function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {

    // silent
  }
}

/**
 * Generic API fetch helper (for future backend mode).
 * Not used yet — placeholder for Fase 3.
 */
export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json'
      // TODO: Add auth token header when backend is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
    ...options
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}